/* ─────────────────────────────────────────────────────────────────────────────
   Courier Setup (supply-side) RTK Query slice — the single data layer for the
   module. Endpoint PATHS come from apiConstants (constants.js); transport uses the
   shared axios helpers (get/postGeneric/patch/putGeneric/deleteGeneric/postMultipart).
   Replaces the old bespoke supply-side/api.js service object.

   Every endpoint runs through run(): unwrap()s the GenericAPIResponse envelope and
   maps errors to {message} via apiError(). Search endpoints return ResultData (the
   {data,count} body → its `data` array after unwrap).
   ──────────────────────────────────────────────────────────────────────────── */
import {createApi} from '@reduxjs/toolkit/query/react'
import {
  apiConstants,
  get,
  post,
  postGeneric,
  patch,
  putGeneric,
  deleteGeneric,
  postMultipart,
} from '../common/constants'
import {unwrap, apiError} from '../pages/supply-side/supplyMeta'

const PARENT = apiConstants.COURIER_PARENT // 'courierPartner/parent'
const AGREEMENT = apiConstants.COURIER_PARENT_AGREEMENT // 'courierPartner/parent/agreement'
const ZONEMAP = apiConstants.ZONEMAP // 'zonemap'
/* PCIS serviceability coverage base path (inlined — not added to constants.js) */
const SERVICEABILITY = 'pcis-admin/serviceability/pincodes'

/* run an axios call → RTK Query {data}|{error}, preserving unwrap()/apiError() behaviour */
const run = async (promise) => {
  try {
    const res = await promise
    /* Prozo's GenericAPIResponse returns HTTP 200 even for logical failures,
       flagging them with `status: "ERROR"` in the body (the same convention used
       elsewhere in the app, e.g. DwsConfig checks `res.data.status !== "ERROR"`).
       Without this guard a failed upload/save resolves as success and shows a green
       toast — so surface it as a real error and let apiError() pull the exact
       backend message out of the envelope. */
    if (res && res.data && res.data.status === 'ERROR') {
      return {error: {status: 'CUSTOM_ERROR', message: apiError({response: res}), data: res.data}}
    }
    return {data: unwrap(res)}
  } catch (e) {
    return {error: {status: e?.response?.status ?? 'CUSTOM_ERROR', message: apiError(e), data: e?.response?.data}}
  }
}

/* run a pcis-admin/* axios call → RTK Query {data}|{error}. This proxy's response
   envelope is {meta, result}, not Prozo's own GenericAPIResponse — so, unlike run(),
   this reads `result` directly rather than going through unwrap(). */
const runPcis = async (promise) => {
  try {
    const res = await promise
    return {data: (res && res.data && res.data.result) ?? res?.data}
  } catch (e) {
    return {error: {status: e?.response?.status ?? 'CUSTOM_ERROR', message: apiError(e), data: e?.response?.data}}
  }
}

/* like runPcis, but the serviceability endpoints can return HTTP 200 with
   meta.success=false — surface meta.message as an error in that case. */
const runPcisEnv = async (promise) => {
  try {
    const res = await promise
    const meta = res && res.data && res.data.meta
    if (meta && meta.success === false) {
      return {error: {status: meta.status ?? 'CUSTOM_ERROR', message: meta.message || 'Request failed', data: res.data}}
    }
    return {data: (res && res.data && res.data.result) ?? res?.data}
  } catch (e) {
    return {error: {status: e?.response?.status ?? 'CUSTOM_ERROR', message: apiError(e), data: e?.response?.data}}
  }
}

const multipart = (endpoint, parts) => {
  const fd = new FormData()
  Object.keys(parts).forEach((k) => {
    const v = parts[k]
    if (Array.isArray(v)) v.forEach((item) => fd.append(k, item))
    else fd.append(k, v)
  })
  return postMultipart(endpoint, fd)
}

/* ── PCIS dashboard ─────────────────────────────────────────────────────────
   The dashboard puts ONE filter rail above five tabs, so all eight aggregate
   endpoints take the identical filter object. Serializing it in one place is what
   keeps that promise honest — a tab that dropped `merchant` from its params would
   quietly render numbers that disagree with the tab beside it.

   Empty values are omitted rather than sent blank. The backend treats "" and
   absent the same, but omitting keeps the URL stable, which keeps both RTK
   Query's cache key and the backend's own 60s cache key stable. */
/* One index-only count of Proship's B2C orders in a window. Shared by proshipOrderCount (whole
   window, inclusive end) and proshipOrderCountSeries (per bucket, exclusive end) so the two can
   never drift in which predicates they apply — the subtraction against PCIS native volume is only
   valid while both sides count the same thing.

   Dates go as FilterCondition's Date('yyyy-MM-dd HH:mm:ss') wrapper, which the backend parses into
   a real Date; the dashboard rail emits yyyy-MM-dd'T'HH:mm:ss, hence the swap. */
const asFilterDate = (s) => `Date('${String(s || '').replace('T', ' ')}')`

const countProshipB2C = async (from, to, endOperator) => {
  const res = await post(apiConstants.ORDER_SEARCH, {
    countOnly: true,
    andfilter: [
      {field: 'awbRegisteredDate', operator: 'gte', value: asFilterDate(from)},
      {field: 'awbRegisteredDate', operator: endOperator, value: asFilterDate(to)},
      {field: 'shipmentType', operator: 'eq', value: 'B2C'},
    ],
  })
  /* Read `count` explicitly rather than going through unwrap(): a countOnly response carries the
     number on ResultData.count with an empty `data` array, and unwrap() would return that array
     (empty array is not nullish) and lose the count. */
  const count = res?.data?.count ?? res?.data?.responseObj?.count
  return Number(count) || 0
}

const healthParams = (f = {}) => {
  const p = {}
  const put = (k, v) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) p[k] = v
  }
  put('from', f.from)
  put('to', f.to)
  put('courier', f.courier)
  put('account', f.account)
  put('merchant', f.merchant)
  put('channel', f.channel)
  put('routingMode', f.routingMode)
  put('orderType', f.orderType)
  put('deliveryType', f.deliveryType)
  put('onlyFailed', f.onlyFailed)
  return p
}

/* Spread into the endpoints() map. Each is a plain GET through runPcis — these are
   read-only aggregates, so none of them provides or invalidates a tag. */
const pcisDashboardEndpoints = (b) => ({
  /* Every Overview KPI plus deltas vs the preceding window, in one request. Use
     this rather than assembling cards from the endpoints below: one call means
     every number describes the same instant. */
  pcisHealthOverview: b.query({
    queryFn: (f) => runPcis(get(apiConstants.PCIS_HEALTH_OVERVIEW, healthParams(f))),
  }),
  /* Volume/failure trend. Gap-filled server-side, so the points array can be fed
     straight to a chart. `bucket` is optional — omit it and the backend picks a
     granularity from the window, reported back as response.bucket. */
  pcisHealthTimeseries: b.query({
    queryFn: ({bucket, ...f} = {}) => {
      const params = healthParams(f)
      if (bucket) params.bucket = bucket
      return runPcis(get(apiConstants.PCIS_HEALTH_TIMESERIES, params))
    },
  }),
  /* Counts grouped by COURIER | ACCOUNT | MERCHANT | CHANNEL | ROUTING | PAYMENT |
     DIRECTION | STATUS | PLACEMENT. One endpoint behind several widgets. */
  pcisHealthBreakdown: b.query({
    queryFn: ({dimension = 'COURIER', limit = 50, ...f} = {}) =>
      runPcis(get(apiConstants.PCIS_HEALTH_BREAKDOWN, {...healthParams(f), dimension, limit})),
  }),
  /* Failure families, retry-recovery stats and the invoice value behind failures. */
  pcisHealthFailures: b.query({
    queryFn: (f) => runPcis(get(apiConstants.PCIS_HEALTH_FAILURES, healthParams(f))),
  }),
  /* Current-status roll-up + inferred forward funnel. Read nativeCoveragePct with
     it — order_status is only maintained for PCIS-native orders. */
  pcisHealthLifecycle: b.query({
    queryFn: (f) => runPcis(get(apiConstants.PCIS_HEALTH_LIFECYCLE, healthParams(f))),
  }),
  /* Placement latency percentiles, fleet-wide and per courier. */
  pcisHealthLatency: b.query({
    queryFn: ({limit = 50, ...f} = {}) =>
      runPcis(get(apiConstants.PCIS_HEALTH_LATENCY, {...healthParams(f), limit})),
  }),
  /* Migration state: what's configured NATIVE vs what share of volume that won. Reflects each
     account's CURRENT config — an account switched today changes what this reports for past
     orders too. */
  pcisHealthAdoption: b.query({
    queryFn: (f) => runPcis(get(apiConstants.PCIS_HEALTH_ADOPTION, healthParams(f))),
  }),
  /* NATIVE (Direct Integration) order counts for the window, parent -> courier -> account.
     Point-in-time: each order keeps the routing PCIS actually applied when it was placed, so
     this stays accurate after a later NATIVE/CLICKPOST_FALLBACK config switch.

     Successful placements only, windowed on awbRegisteredDate — the same basis as
     proshipOrderCount below, which is what makes the subtraction valid. Read `nativeOrders`
     only: PCIS does not store Clickpost orders, so `totalOrders` equals it and
     `clickpostOrders` is always 0 (absence of data, NOT absence of Clickpost volume). */
  pcisNativeVolume: b.query({
    queryFn: ({from, to} = {}) => runPcis(get(apiConstants.PCIS_NATIVE_VOLUME, {from, to})),
  }),
  /* Direct Integration vs Clickpost, per courier and per time bucket, from Proship's own orders.
     Supersedes the pcisNativeVolume + proshipOrderCount pair for everything routing-related: that
     pair could only ever give a fleet-wide split, because PCIS has no Clickpost orders to break
     down and Proship's per-courier grouping was a 190s full fetch until the covering index landed.

     One request, four views (see the endpoint's DTO): totals, byCourier, buckets and
     courierBuckets. Because courierBuckets ships in the same payload, applying a courier filter
     is a filter over a few hundred pre-aggregated rows here in the client — no second round trip,
     and no per-bucket fan-out of count calls. */
  pcisRoutingSplit: b.query({
    queryFn: ({from, to, bucket} = {}) =>
      runPcis(get(apiConstants.PCIS_ROUTING_SPLIT, bucket ? {from, to, bucket} : {from, to})),
  }),
  /* Proship's own count of orders for the window — the denominator the native figure above is
     subtracted from, giving Clickpost volume (PCIS cannot report that; it only stores native).

     Deliberately just two predicates. Both live in the existing
     awbRegisteredDate_1_merchantId_1_shipmentType_1_orderSubStatus_1 index, so this is an
     index-only count. Adding orderStatus or awbNumber pushes it into a FETCH of every order in
     the range (measured at ~190s for a 30-day window on prod) — and neither is needed:
     awbRegisteredDate is stamped only on successful placement (verified: 100% of matching orders
     have an awbNumber), and never-started orders are UNPROCESSED with no awbRegisteredDate at
     all, so both are already excluded. B2C only — B2B never reaches PCIS, so counting it here
     would inflate the Clickpost side. */
  proshipOrderCount: b.query({
    queryFn: async ({from, to} = {}) => {
      try {
        return {data: {count: await countProshipB2C(from, to, 'lte')}}
      } catch (e) {
        return {error: {status: e?.response?.status ?? 'CUSTOM_ERROR', message: apiError(e), data: e?.response?.data}}
      }
    },
  }),
  /* Same count as above, once per chart bucket — the per-bucket Proship total the DI-vs-Clickpost
     trend needs. Each call is the identical two-predicate index-only count, just over a narrower
     range, so N of them cost no more disk work in total than the single full-window count; they
     are only separate HTTP calls. Kept to CONCURRENCY at a time so a 30-bucket window doesn't
     open 30 sockets at once.

     Half-open [from, to) via `lt`, so adjacent buckets can't double-count an order sitting exactly
     on a boundary — unlike the whole-window count above, where an inclusive end is what we want.

     `buckets` is [{label, from, to}]; the response mirrors it as {label: count | null} so the caller
     doesn't depend on ordering, plus `missing` — how many buckets failed.

     PER-BUCKET failure handling, deliberately. This used to run the whole set through Promise.all
     and surface any single rejection as a failure of the entire query, on the reasoning that a
     short count would read as a real dip. In practice the consequence was worse: with 24 or 30
     buckets, one failing call blanked the Clickpost series for every bucket, and the chart then
     showed DI-only bars with nothing to say the other half was missing — a confident, wrong 100%.
     Now a failed bucket is null (the chart draws a gap and the panel says how many) while every
     bucket that did answer is still plotted.

     allSettled per chunk, and one retry for a failed bucket, since these are short reads and the
     common failure is transient. The real fix is the routing-split endpoint, which returns every
     bucket in one response — this whole endpoint goes away with it. */
  proshipOrderCountSeries: b.query({
    queryFn: async ({buckets} = {}) => {
      const list = Array.isArray(buckets) ? buckets : []
      if (!list.length) return {data: {counts: {}, missing: 0}}
      const CONCURRENCY = 4
      const counts = {}
      let missing = 0
      for (let i = 0; i < list.length; i += CONCURRENCY) {
        const slice = list.slice(i, i + CONCURRENCY)
        // eslint-disable-next-line no-await-in-loop
        const settled = await Promise.allSettled(
          slice.map((b2) =>
            countProshipB2C(b2.from, b2.to, 'lt').catch(() => countProshipB2C(b2.from, b2.to, 'lt'))
          )
        )
        for (let j = 0; j < settled.length; j += 1) {
          const r = settled[j]
          counts[slice[j].label] = r.status === 'fulfilled' ? r.value : null
          if (r.status !== 'fulfilled') missing += 1
        }
      }
      return {data: {counts, missing}}
    },
  }),
  /* Status-feed health + the unmapped-courier-status backlog. Scoped by event
     ingest time, so courier/account filters do NOT narrow this one. */
  pcisHealthEvents: b.query({
    queryFn: ({unmappedLimit = 20, ...f} = {}) =>
      runPcis(get(apiConstants.PCIS_HEALTH_EVENTS, {...healthParams(f), unmappedLimit})),
  }),
})

export const supplyApiSlice = createApi({
  reducerPath: 'supplyApi',
  baseQuery: () => ({data: undefined}), // unused — every endpoint defines its own queryFn
  keepUnusedDataFor: 5 * 60,
  tagTypes: ['Parent', 'Code', 'Agreement', 'Dashboard', 'StatusMapping', 'PcisCache', 'BlueDartOriginArea', 'ErrorMapping'],
  endpoints: (b) => ({
    /* ── Parents ─────────────────────────────────────────────────────────── */
    listParents: b.query({
      queryFn: () => run(get(`${PARENT}/list`)),
      providesTags: [{type: 'Parent', id: 'LIST'}],
    }),
    getParent: b.query({
      queryFn: (id) => run(get(`${PARENT}/${id}`)),
      providesTags: (r, e, id) => [{type: 'Parent', id}],
    }),
    searchParents: b.query({
      queryFn: (wrapper) => run(postGeneric(`${PARENT}/search`, wrapper)),
      providesTags: [{type: 'Parent', id: 'LIST'}],
    }),
    dashboard: b.query({
      queryFn: () => run(get(`${PARENT}/dashboard`)),
      providesTags: [{type: 'Dashboard', id: 'STATS'}],
    }),
    parentRateCards: b.query({
      queryFn: (id) => run(get(`${PARENT}/${id}/rateCards`)),
      providesTags: (r, e, id) => [{type: 'Parent', id}],
    }),
    courierInvoices: b.query({
      queryFn: (params) => run(get(apiConstants.COURIER_ORDER_INVOICE_LIST, params)),
    }),
    createParent: b.mutation({
      queryFn: (details) => run(postGeneric(`${PARENT}`, details)),
      invalidatesTags: [{type: 'Parent', id: 'LIST'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    updateParent: b.mutation({
      queryFn: ({id, details}) => run(patch(`${PARENT}/${id}`, details)),
      invalidatesTags: (r, e, {id}) => [{type: 'Parent', id}, {type: 'Parent', id: 'LIST'}],
    }),
    saveContacts: b.mutation({
      queryFn: ({id, contacts}) => run(putGeneric(`${PARENT}/${id}/contacts`, {contacts})),
      invalidatesTags: (r, e, {id}) => [{type: 'Parent', id}, {type: 'Parent', id: 'LIST'}],
    }),
    updateParentStatus: b.mutation({
      queryFn: ({id, status, reason}) => run(patch(`${PARENT}/${id}/status`, {status, reason})),
      invalidatesTags: (r, e, {id}) => [{type: 'Parent', id}, {type: 'Parent', id: 'LIST'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    deleteParent: b.mutation({
      queryFn: (id) => run(deleteGeneric(`${PARENT}/${id}`)),
      invalidatesTags: [{type: 'Parent', id: 'LIST'}, {type: 'Dashboard', id: 'STATS'}],
    }),

    /* ── Codes (existing courierPartner APIs) ────────────────────────────── */
    searchCouriers: b.query({
      queryFn: (wrapper) => run(postGeneric(apiConstants.COURIER_PARTNER_SEARCH, wrapper)),
      providesTags: [{type: 'Code', id: 'LIST'}],
    }),
    // Cached variant — returns the full courier list ({data:[...]}); the non-cached
    // /search can come back empty on some environments. Used for pickers/dropdowns.
    searchCouriersCached: b.query({
      queryFn: (wrapper) => run(postGeneric(apiConstants.COURIER_PARTNER_SEARCH_CACHED, wrapper)),
      providesTags: [{type: 'Code', id: 'LIST'}],
    }),
    codesByParent: b.query({
      queryFn: (parentId) =>
        run(
          postGeneric(apiConstants.COURIER_PARTNER_SEARCH, {
            offset: 0,
            limit: 200,
            andfilter: [
              {field: 'parent_id', operator: 'eq', value: parentId},
              {field: 'is_deleted', operator: 'eq', value: '0'},
            ],
          })
        ),
      providesTags: (r, e, parentId) => [{type: 'Code', id: parentId}],
    }),
    /* Cached variant of codesByParent (courierPartner/searchCached) — used by the
       PCIS Add-account dropdown; /search can return empty on some environments. */
    codesByParentCached: b.query({
      queryFn: (parentId) =>
        run(
          postGeneric(apiConstants.COURIER_PARTNER_SEARCH_CACHED, {
            offset: 0,
            limit: 500,
            andfilter: [
              {field: 'parent_id', operator: 'eq', value: parentId},
              {field: 'is_deleted', operator: 'eq', value: '0'},
            ],
          })
        ),
      providesTags: (r, e, parentId) => [{type: 'Code', id: parentId}],
    }),
    createCourier: b.mutation({
      queryFn: (courier) => run(postGeneric(apiConstants.CREATE_COURIER_PARTNER, courier)),
      invalidatesTags: [{type: 'Code', id: 'LIST'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    updateCourier: b.mutation({
      queryFn: (courier) => run(patch(apiConstants.COURIERS, courier)),
      invalidatesTags: [{type: 'Code', id: 'LIST'}],
    }),
    /* Courier Code Display — recompute the derived logical_name for every live courier (PROSHIPADMIN only).
       Envelope is MongoGenericResponse {meta, result}; result = {total, updated, unchanged, unnamed,
       unnamedAccountCodes, requiredSegments}. Read `result` directly (not unwrap) so the counts survive. */
    backfillLogicalNames: b.mutation({
      queryFn: async () => {
        try {
          const res = await postGeneric(apiConstants.COURIER_LOGICAL_NAME_BACKFILL, {})
          return {data: (res && res.data && res.data.result) || {}}
        } catch (e) {
          return {error: {status: e?.response?.status ?? 'CUSTOM_ERROR', message: apiError(e), data: e?.response?.data}}
        }
      },
      invalidatesTags: [{type: 'Code', id: 'LIST'}],
    }),
    /* PCIS/Direct-Integration courier account — proxied through ProShip's own backend
       (BASE_LOCAL, standard authenticated call — no separate host/admin key anymore),
       used only when the onboarding wizard's Click Post / Direct Integration step
       picked DIRECT, attaching a new account to an ALREADY-REGISTERED PCIS courier
       partner. */
    createCourierAccount: b.mutation({
      queryFn: (account) => runPcis(postGeneric(apiConstants.PCIS_COURIER_ACCOUNTS, account)),
      // also refresh the PCIS accounts list (Manage/Health pages) after an add
      invalidatesTags: [{type: 'Code', id: 'LIST'}, {type: 'Code', id: 'PCIS_ACCOUNTS'}],
    }),
    /* PCIS courier-partners registration — used instead of createCourierAccount when
       onboarding a brand-new parent + Direct Integration, since the courier doesn't
       exist in PCIS yet (nothing to attach an account to). */
    createCourierPartner: b.mutation({
      queryFn: (partner) => runPcis(postGeneric(apiConstants.PCIS_COURIER_PARTNERS, partner)),
      invalidatesTags: [{type: 'Code', id: 'LIST'}],
    }),
    /* PCIS courier-partners lookup — returns every existing entry for a courier code
       under the given service type (FORWARD or RVP — reverse-with-QC and reverse-
       without-QC both send RVP), e.g. multiple DTDC rows, each with a different
       cp_id; the DI step lets the user pick the right one. */
    lookupCourierPartners: b.query({
      queryFn: ({courierPartnerCode, serviceType}) =>
        runPcis(get(`${apiConstants.PCIS_COURIER_PARTNERS}/${encodeURIComponent(courierPartnerCode)}`, {service_type: serviceType})),
    }),
    /* PCIS courier-accounts list — powers the account filter dropdown on the Health
       Summary/Details pages and the Manage page's account picker. `parent` narrows
       to one parent courier's accounts (?parent=<name>); omitted, it returns every account. */
    listPcisCourierAccounts: b.query({
      queryFn: (parent) => runPcis(get(apiConstants.PCIS_COURIER_ACCOUNTS, parent ? {parent} : {})),
      providesTags: [{type: 'Code', id: 'PCIS_ACCOUNTS'}],
    }),
    /* Switch an account's integration_type NATIVE ↔ CLICKPOST_FALLBACK. value goes
       in the query string (no request body); the backend evicts its cache so the
       change takes effect on the next order. Invalidates the accounts list so the
       Manage table reflects the new value immediately. */
    switchAccountIntegrationType: b.mutation({
      queryFn: ({accountCode, value}) =>
        runPcis(postGeneric(`${apiConstants.PCIS_COURIER_ACCOUNTS}/${encodeURIComponent(accountCode)}/integration-type?value=${encodeURIComponent(value)}`, null)),
      invalidatesTags: [{type: 'Code', id: 'PCIS_ACCOUNTS'}],
    }),
    /* One account by code — includes its `credentials` object (for the edit form). */
    getPcisCourierAccount: b.query({
      queryFn: (accountCode) => runPcis(get(`${apiConstants.PCIS_COURIER_ACCOUNTS}/${encodeURIComponent(accountCode)}`)),
      providesTags: (r, e, accountCode) => [{type: 'Code', id: `PCIS_ACCT_${accountCode}`}],
    }),
    /* Update an account (PATCH — partial). Used to edit credentials from the UI:
       body = {credentials: {...}} (merged). After this, the adapter registry must
       be refreshed for PCIS to pick up the change (see refreshAdapterRegistry). */
    updatePcisCourierAccount: b.mutation({
      queryFn: ({accountCode, body}) => runPcis(patch(`${apiConstants.PCIS_COURIER_ACCOUNTS}/${encodeURIComponent(accountCode)}`, body)),
      invalidatesTags: (r, e, {accountCode}) => [{type: 'Code', id: 'PCIS_ACCOUNTS'}, {type: 'Code', id: `PCIS_ACCT_${accountCode}`}],
    }),
    /* Rebuild PCIS's in-memory adapter registry so credential/account changes take
       effect immediately (idempotent). Called right after a credentials update. */
    refreshAdapterRegistry: b.mutation({
      queryFn: () => runPcis(postGeneric('pcis-admin/adapter-registry/refresh', null)),
    }),
    /* PCIS Health — Summary: aggregate rollup for a window (from/to ISO local
       date-time; omitted = last 60 minutes), optionally scoped to a courier/account. */
    pcisHealthSummary: b.query({
      queryFn: ({from, to, courier, account} = {}) => {
        const params = {}
        if (from) params.from = from
        if (to) params.to = to
        if (courier) params.courier = courier
        if (account) params.account = account
        return runPcis(get(apiConstants.PCIS_HEALTH_SUMMARY, params))
      },
    }),
    /* PCIS Health — Details: paginated order-level rows under the same filter
       window/scope as Summary, plus onlyFailed. page is 0-based; size max 100. */
    pcisHealthOrders: b.query({
      queryFn: ({from, to, courier, account, onlyFailed, page = 0, size = 100} = {}) => {
        const params = {page, size}
        if (from) params.from = from
        if (to) params.to = to
        if (courier) params.courier = courier
        if (account) params.account = account
        if (onlyFailed) params.onlyFailed = true
        return runPcis(get(apiConstants.PCIS_HEALTH_ORDERS, params))
      },
    }),

    /* ── PCIS Dashboard aggregates ───────────────────────────────────────── */
    /* All eight share one filter shape, so they share one param serializer. Only
       keys with a value are sent — the backend reads a missing param and an empty
       one identically, but omitting keeps the URL (and therefore the RTK Query
       cache key, and the backend's own 60s cache key) stable across renders. */
    ...pcisDashboardEndpoints(b),

    /* ── PCIS Serviceability coverage (pcis-admin/serviceability/pincodes) ── */
    /* Upload a pincode CSV for one or more accounts. `parts` is the multipart body
       (file + account_code[] + direction/payment_mode/replace_existing/dry_run/…).
       dry_run=true validates + reports counts without writing. */
    uploadServiceabilityPincodes: b.mutation({
      queryFn: (parts) => runPcisEnv(multipart(SERVICEABILITY, parts)),
      invalidatesTags: (r, e, parts) => (parts && parts.dry_run === 'true' ? [] : [{type: 'Code', id: 'SERVICEABILITY'}]),
    }),
    /* Verify a pickup→drop lane. With account_code → that one account; without →
       all serviceable accounts grouped by payment mode. Reads the table only. */
    checkServiceabilityLane: b.query({
      queryFn: ({pickup, drop, account_code}) => {
        const params = {pickup, drop}
        if (account_code) params.account_code = account_code
        return runPcisEnv(get(`${SERVICEABILITY}/check`, params))
      },
    }),
    /* Whether an account's coverage table is loaded + its row count (for a badge). */
    serviceabilityStatus: b.query({
      queryFn: (account_code) => runPcisEnv(get(`${SERVICEABILITY}/status`, {account_code})),
      providesTags: (r, e, account_code) => [{type: 'Code', id: 'SERVICEABILITY'}, {type: 'Code', id: `SVC_${account_code}`}],
    }),
    /* Clear an account's (or cp_id's) coverage — reverts to live serviceability. */
    clearServiceability: b.mutation({
      queryFn: ({account_code, cp_id}) => {
        const qs = account_code ? `account_code=${encodeURIComponent(account_code)}` : `cp_id=${encodeURIComponent(cp_id)}`
        return runPcisEnv(deleteGeneric(`${SERVICEABILITY}?${qs}`))
      },
      invalidatesTags: [{type: 'Code', id: 'SERVICEABILITY'}],
    }),

    /* ── PCIS Status Mappings (pcis-admin/status-mappings) ──────────────────
       ProShip is the authority for pcis_status_code/description (from OrderStatusEnum);
       PCIS stores what we validate and audits every change (who = the JWT user, forwarded). */
    pcisStatusCodes: b.query({
      queryFn: () => runPcis(get('pcis-admin/status-codes')),
    }),
    pcisShipmentTypes: b.query({
      queryFn: () => runPcis(get('pcis-admin/status-mappings/shipment-types')),
    }),
    pcisStatusCouriers: b.query({
      queryFn: () => runPcis(get('pcis-admin/status-mappings/couriers')),
      providesTags: [{type: 'StatusMapping', id: 'COURIERS'}],
    }),
    pcisStatusMappings: b.query({
      queryFn: (courierCode) => runPcis(get(`pcis-admin/status-mappings/${encodeURIComponent(courierCode)}`)),
      providesTags: (r, e, courierCode) => [{type: 'StatusMapping', id: courierCode}, {type: 'StatusMapping', id: 'LIST'}],
    }),
    upsertPcisStatusMapping: b.mutation({
      queryFn: (body) => runPcis(postGeneric('pcis-admin/status-mappings', body)),
      invalidatesTags: [{type: 'StatusMapping', id: 'LIST'}, {type: 'StatusMapping', id: 'COURIERS'}],
    }),
    deletePcisStatusMapping: b.mutation({
      queryFn: ({id}) => runPcis(deleteGeneric(`pcis-admin/status-mappings/${id}`)),
      invalidatesTags: [{type: 'StatusMapping', id: 'LIST'}],
    }),
    uploadPcisStatusMappings: b.mutation({
      queryFn: ({file}) => runPcis(multipart('pcis-admin/status-mappings/upload', {file})),
      invalidatesTags: [{type: 'StatusMapping', id: 'LIST'}, {type: 'StatusMapping', id: 'COURIERS'}],
    }),

    /* ── PCIS BlueDart Origin Area (pcis-admin/bluedart-origin-area) ──────────
       Ops-maintained pincode → BlueDart origin/billing area. Paginated list (~13k rows) with an
       optional pincode filter; inline CRUD + bulk Excel, exactly like status mappings. */
    pcisBlueDartOriginAreas: b.query({
      queryFn: ({pincode = '', page = 0, size = 50} = {}) =>
        runPcis(get('pcis-admin/bluedart-origin-area', {pincode, page, size})),
      providesTags: [{type: 'BlueDartOriginArea', id: 'LIST'}],
    }),
    upsertPcisBlueDartOriginArea: b.mutation({
      queryFn: (body) => runPcis(postGeneric('pcis-admin/bluedart-origin-area', body)),
      invalidatesTags: [{type: 'BlueDartOriginArea', id: 'LIST'}],
    }),
    deletePcisBlueDartOriginArea: b.mutation({
      queryFn: ({id}) => runPcis(deleteGeneric(`pcis-admin/bluedart-origin-area/${id}`)),
      invalidatesTags: [{type: 'BlueDartOriginArea', id: 'LIST'}],
    }),
    uploadPcisBlueDartOriginArea: b.mutation({
      queryFn: ({file}) => runPcis(multipart('pcis-admin/bluedart-origin-area/upload', {file})),
      invalidatesTags: [{type: 'BlueDartOriginArea', id: 'LIST'}],
    }),

    /* ── PCIS Error Code Mapping (pcis-admin/courier-error-mappings) ──────────
       Courier error (code/keyword) → PCIS standard sub_code. sub_code dropdown is PCIS's own
       error-code catalogue (/error-codes). Inline CRUD + bulk Excel, like status mappings. */
    pcisErrorCodes: b.query({
      queryFn: () => runPcis(get('pcis-admin/error-codes')),
    }),
    pcisErrorMappingCouriers: b.query({
      queryFn: () => runPcis(get('pcis-admin/courier-error-mappings/couriers')),
      providesTags: [{type: 'ErrorMapping', id: 'COURIERS'}],
    }),
    pcisErrorMappings: b.query({
      queryFn: (courierCode) => runPcis(get(`pcis-admin/courier-error-mappings/${encodeURIComponent(courierCode)}`)),
      providesTags: (r, e, courierCode) => [{type: 'ErrorMapping', id: courierCode}, {type: 'ErrorMapping', id: 'LIST'}],
    }),
    upsertPcisErrorMapping: b.mutation({
      queryFn: (body) => runPcis(postGeneric('pcis-admin/courier-error-mappings', body)),
      invalidatesTags: [{type: 'ErrorMapping', id: 'LIST'}, {type: 'ErrorMapping', id: 'COURIERS'}],
    }),
    deletePcisErrorMapping: b.mutation({
      queryFn: ({id}) => runPcis(deleteGeneric(`pcis-admin/courier-error-mappings/${id}`)),
      invalidatesTags: [{type: 'ErrorMapping', id: 'LIST'}],
    }),
    uploadPcisErrorMappings: b.mutation({
      queryFn: ({file}) => runPcis(multipart('pcis-admin/courier-error-mappings/upload', {file})),
      invalidatesTags: [{type: 'ErrorMapping', id: 'LIST'}, {type: 'ErrorMapping', id: 'COURIERS'}],
    }),

    /* ── PCIS Cache management (pcis-admin/cache) — inspect + evict Redis caches.
       No flush-all by design; purge-everything is a pcis:* glob delete. */
    pcisCacheCatalog: b.query({
      queryFn: () => runPcis(get('pcis-admin/cache')),
      providesTags: [{type: 'PcisCache', id: 'CATALOG'}],
    }),
    pcisCacheKeys: b.query({
      queryFn: ({prefix, limit = 200, values = false}) =>
        runPcis(get('pcis-admin/cache/keys', {prefix, limit, values})),
      providesTags: [{type: 'PcisCache', id: 'KEYS'}],
    }),
    pcisCacheEntry: b.query({
      queryFn: (key) => runPcis(get('pcis-admin/cache/entry', {key})),
    }),
    deletePcisCacheByPattern: b.mutation({
      queryFn: (pattern) => runPcis(deleteGeneric(`pcis-admin/cache/keys?pattern=${encodeURIComponent(pattern)}`)),
      invalidatesTags: [{type: 'PcisCache', id: 'CATALOG'}, {type: 'PcisCache', id: 'KEYS'}],
    }),
    clearPcisSpringCache: b.mutation({
      queryFn: (cacheName) => runPcis(deleteGeneric(`pcis-admin/cache/${encodeURIComponent(cacheName)}`)),
      invalidatesTags: [{type: 'PcisCache', id: 'CATALOG'}, {type: 'PcisCache', id: 'KEYS'}],
    }),

    /* ── Agreements ──────────────────────────────────────────────────────── */
    agreementTimeline: b.query({
      queryFn: (parentId) => run(get(`${AGREEMENT}/timeline/${parentId}`)),
      providesTags: (r, e, parentId) => [{type: 'Agreement', id: parentId}],
    }),
    searchAgreements: b.query({
      queryFn: (wrapper) => run(postGeneric(`${AGREEMENT}/search`, wrapper)),
      providesTags: [{type: 'Agreement', id: 'LIST'}],
    }),
    downloadAgreement: b.query({
      queryFn: ({id, stage}) => run(get(`${AGREEMENT}/${id}/download`, stage ? {stage} : {})),
    }),
    uploadAgreement: b.mutation({
      queryFn: ({request, file}) => run(multipart(`${AGREEMENT}`, {request: JSON.stringify(request), file})),
      invalidatesTags: [{type: 'Agreement'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    signAgreement: b.mutation({
      queryFn: ({id, file}) => run(multipart(`${AGREEMENT}/${id}/sign`, {file})),
      invalidatesTags: [{type: 'Agreement'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    approveAgreement: b.mutation({
      queryFn: (id) => run(postGeneric(`${AGREEMENT}/${id}/approve`, {})),
      invalidatesTags: [{type: 'Agreement'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    rejectAgreement: b.mutation({
      queryFn: ({id, reason}) => run(postGeneric(`${AGREEMENT}/${id}/reject`, {reason})),
      invalidatesTags: [{type: 'Agreement'}, {type: 'Dashboard', id: 'STATS'}],
    }),
    supersedeAgreement: b.mutation({
      queryFn: ({id, reason}) => run(postGeneric(`${AGREEMENT}/${id}/supersede`, {reason})),
      invalidatesTags: [{type: 'Agreement'}, {type: 'Dashboard', id: 'STATS'}],
    }),

    /* ── Onboarding commit uploads (existing tools APIs) ─────────────────── */
    uploadServiceability: b.mutation({
      queryFn: ({file, confirm, courierAccountCodes, courierParent, pincodeType}) =>
        run(
          multipart(apiConstants.BULK_SERV_UPLOAD, {
            file,
            confirm: String(!!confirm),
            courierAccountCodes,
            courierParent,
            pincodeType,
          })
        ),
    }),
    uploadServicabilityFile: b.mutation({
      // effectiveFrom (yyyy-MM-dd, WEIGHT_PRICE only): future date → staged pending (daily cron flips it live);
      // omitted/today/past → immediate replace. TAT is always immediate.
      queryFn: ({prozoId, type, file, effectiveFrom}) =>
        run(multipart(apiConstants.SERVICEABILITY_UPLOAD, effectiveFrom ? {prozoId, type, file, effectiveFrom} : {prozoId, type, file})),
    }),
    cancelPendingRateCard: b.mutation({
      queryFn: (prozoId) =>
        run(postGeneric(`${apiConstants.CANCEL_PENDING_RATECARD}?prozoId=${encodeURIComponent(prozoId)}`, {})),
    }),
    saveQcConfig: b.mutation({
      queryFn: ({prozoId, qcConfig}) =>
        run(postGeneric(`${apiConstants.COURIER_QC_CONFIG}?prozoId=${encodeURIComponent(prozoId)}`, qcConfig)),
    }),

    /* ── CP Zone Mapping (supply/zone-mapping) — base path apiConstants.ZONEMAP ─── */
    zoneSources: b.query({queryFn: () => run(get(`${ZONEMAP}/sources`))}),
    zoneGetSetup: b.query({queryFn: (code) => run(get(`${ZONEMAP}/setup`, {code}))}),
    // parent-level base state by parent name — shown before any account is picked
    zoneParentBase: b.query({queryFn: (parent) => run(get(`${ZONEMAP}/parentBase`, {parent}))}),
    zonePincodeOptions: b.query({queryFn: ({mode, q}) => run(get(`${ZONEMAP}/pincodeOptions`, {mode, q}))}),
    zoneResolveCount: b.query({queryFn: (body) => run(postGeneric(`${ZONEMAP}/resolveCount`, body))}),
    zoneTest: b.query({queryFn: ({code, body}) => run(postGeneric(`${ZONEMAP}/test?code=${encodeURIComponent(code)}`, body))}),
    zoneJob: b.query({queryFn: (jobId) => run(get(`${ZONEMAP}/jobs/${encodeURIComponent(jobId)}`))}),
    zoneAddOverride: b.mutation({
      queryFn: ({code, body}) => run(postGeneric(`${ZONEMAP}/overrides?code=${encodeURIComponent(code)}`, body)),
    }),
    zoneDeleteOverride: b.mutation({
      queryFn: ({code, overrideId}) =>
        run(deleteGeneric(`${ZONEMAP}/overrides?code=${encodeURIComponent(code)}&overrideId=${encodeURIComponent(overrideId)}`)),
    }),
    zoneSeedParentBase: b.mutation({queryFn: (body) => run(postGeneric(`${ZONEMAP}/parentBase/seed`, body))}),
    // parent folder re-uploaded straight to S3 → rescan + re-publish every managed child (async chain)
    zoneResyncParent: b.mutation({queryFn: (body) => run(postGeneric(`${ZONEMAP}/parentBase/resync`, body))}),
    zonePublish: b.mutation({
      // full=false → only pickups whose overrides changed; full=true → rebuild every file ("Republish")
      queryFn: ({code, dryRun, full}) => run(postGeneric(`${ZONEMAP}/publish?code=${encodeURIComponent(code)}`, {dryRun: !!dryRun, full: !!full})),
    }),
  }),
})

export const {
  useListParentsQuery,
  useLazyListParentsQuery,
  useGetParentQuery,
  useLazyGetParentQuery,
  useSearchParentsQuery,
  useLazySearchParentsQuery,
  useDashboardQuery,
  useLazyDashboardQuery,
  useParentRateCardsQuery,
  useLazyParentRateCardsQuery,
  useCourierInvoicesQuery,
  useLazyCourierInvoicesQuery,
  useCreateParentMutation,
  useUpdateParentMutation,
  useSaveContactsMutation,
  useUpdateParentStatusMutation,
  useDeleteParentMutation,
  useSearchCouriersQuery,
  useLazySearchCouriersQuery,
  useSearchCouriersCachedQuery,
  useLazySearchCouriersCachedQuery,
  useCodesByParentQuery,
  useLazyCodesByParentQuery,
  useCodesByParentCachedQuery,
  useCreateCourierMutation,
  useUpdateCourierMutation,
  useBackfillLogicalNamesMutation,
  useCreateCourierAccountMutation,
  useCreateCourierPartnerMutation,
  useLazyLookupCourierPartnersQuery,
  useListPcisCourierAccountsQuery,
  useSwitchAccountIntegrationTypeMutation,
  useLazyGetPcisCourierAccountQuery,
  useUpdatePcisCourierAccountMutation,
  useRefreshAdapterRegistryMutation,
  usePcisHealthSummaryQuery,
  usePcisHealthOrdersQuery,
  usePcisHealthOverviewQuery,
  usePcisHealthTimeseriesQuery,
  usePcisHealthBreakdownQuery,
  usePcisHealthFailuresQuery,
  usePcisHealthLifecycleQuery,
  usePcisHealthLatencyQuery,
  usePcisHealthAdoptionQuery,
  usePcisNativeVolumeQuery,
  usePcisRoutingSplitQuery,
  useProshipOrderCountQuery,
  useProshipOrderCountSeriesQuery,
  usePcisHealthEventsQuery,
  useUploadServiceabilityPincodesMutation,
  useLazyCheckServiceabilityLaneQuery,
  useLazyServiceabilityStatusQuery,
  useClearServiceabilityMutation,
  usePcisStatusCodesQuery,
  usePcisShipmentTypesQuery,
  usePcisStatusCouriersQuery,
  usePcisStatusMappingsQuery,
  useLazyPcisStatusMappingsQuery,
  useUpsertPcisStatusMappingMutation,
  useDeletePcisStatusMappingMutation,
  useUploadPcisStatusMappingsMutation,
  usePcisBlueDartOriginAreasQuery,
  useUpsertPcisBlueDartOriginAreaMutation,
  useDeletePcisBlueDartOriginAreaMutation,
  useUploadPcisBlueDartOriginAreaMutation,
  usePcisErrorCodesQuery,
  usePcisErrorMappingCouriersQuery,
  usePcisErrorMappingsQuery,
  useUpsertPcisErrorMappingMutation,
  useDeletePcisErrorMappingMutation,
  useUploadPcisErrorMappingsMutation,
  usePcisCacheCatalogQuery,
  useLazyPcisCacheKeysQuery,
  useLazyPcisCacheEntryQuery,
  useDeletePcisCacheByPatternMutation,
  useClearPcisSpringCacheMutation,
  useAgreementTimelineQuery,
  useLazyAgreementTimelineQuery,
  useSearchAgreementsQuery,
  useLazySearchAgreementsQuery,
  useDownloadAgreementQuery,
  useLazyDownloadAgreementQuery,
  useUploadAgreementMutation,
  useSignAgreementMutation,
  useApproveAgreementMutation,
  useRejectAgreementMutation,
  useSupersedeAgreementMutation,
  useUploadServiceabilityMutation,
  useUploadServicabilityFileMutation,
  useCancelPendingRateCardMutation,
  useSaveQcConfigMutation,
  useLazyZoneSourcesQuery,
  useLazyZoneGetSetupQuery,
  useLazyZoneParentBaseQuery,
  useLazyZonePincodeOptionsQuery,
  useLazyZoneResolveCountQuery,
  useLazyZoneTestQuery,
  useLazyZoneJobQuery,
  useZoneAddOverrideMutation,
  useZoneDeleteOverrideMutation,
  useZoneSeedParentBaseMutation,
  useZoneResyncParentMutation,
  useZonePublishMutation,
} = supplyApiSlice
